/**
 * Convert a 409 IntegrityError from the backend into a field-level form error
 * when the constraint name maps to a known input. Falls back to "handle as
 * banner" when the constraint is composite or unknown.
 *
 * Usage in a mutation onError:
 *   onError: (err) => applyApiErrorToForm(form, err)
 *
 * Returns true if the error was placed on a field (caller should suppress
 * the page-level banner); false to let the caller show the banner.
 */
import type { UseFormReturnType } from '@mantine/form';
import { ApiError } from '../api/client';

/** Constraint name -> form field name. Add entries here as new uniques are created. */
const CONSTRAINT_FIELD_MAP: Record<string, string> = {
  // agreements
  'agreements_service_type_client_number_key': 'client_number',
  'agreements_client_mnemonic_unique_alive':   'client_mnemonic',
  // accounts
  'accounts_iban_unique':                       'iban',
  'accounts_bank_internalnum_unique':           'internal_num',
  // insurance policies
  'insurance_policies_insurer_policy_number_key': 'policy_number',
  // persons (alive-uniques on identifier columns)
  'persons_id_number_unique_alive':             'id_number',
  'persons_tin_1_unique_alive':                 'tin_1',
  // users
  'users_email_unique_alive':                   'email',
  // Composite uniques on join tables (e.g. agreement_parties_unique_alive)
  // intentionally omitted - no single field to attribute them to. Falls back
  // to the page banner.
};

export function applyApiErrorToForm<T>(
  form: UseFormReturnType<T>,
  err: unknown,
): boolean {
  if (!(err instanceof ApiError)) return false;

  // 409 IntegrityError -> field via constraint map.
  if (err.status === 409 && err.code) {
    const field = CONSTRAINT_FIELD_MAP[err.code];
    if (!field) return false;
    // Mantine's setFieldError accepts an arbitrary field path string.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (form as any).setFieldError(field, err.detail);
    return true;
  }

  // 422 Pydantic validation -> map each detail[].loc to a form field.
  //
  // Added 2026-06-09 during A1.f. Previously a Pydantic 422 fell through
  // to the page-banner path with `err.detail` rendered as JSON-ish text
  // and no inline feedback on the offending field — a silent-feeling
  // failure (Pedro's observation: "If the field is required, it should
  // pop on inline a message saying so.").
  //
  // FastAPI/Pydantic's default 422 body shape:
  //   { "detail": [{
  //       "type": "string_too_short",
  //       "loc":  ["body", "internal_num"],
  //       "msg":  "String should have at least 1 character",
  //       "input": "",
  //       "ctx":  { "min_length": 1 }
  //     }, ...] }
  //
  // Each item gets `setFieldError(loc[1], msg)`. Items with locations
  // that don't map to a form field (e.g. ["body"], ["query", ...],
  // or nested object fields) are skipped — but as long as at least one
  // item placed an error, we return true so the page banner is
  // suppressed (caller treats this as a field-level failure).
  //
  // The bespoke password_policy_violation 422 shape — detail is an
  // OBJECT, not an array — is intentionally untouched here; it stays
  // handled by getPasswordPolicyErrors at the call site.
  if (err.status === 422) {
    const body = err.body as { detail?: unknown } | null;
    const items = body?.detail;
    if (!Array.isArray(items)) return false;
    let placedAny = false;
    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as { loc?: unknown; msg?: unknown };
      if (!Array.isArray(item.loc) || typeof item.msg !== 'string') continue;
      if (item.loc[0] !== 'body' || item.loc.length < 2) continue;
      const field = String(item.loc[1]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (form as any).setFieldError(field, item.msg);
      placedAny = true;
    }
    return placedAny;
  }

  return false;
}

/**
 * Decide whether a mutation's error should be shown as a page banner.
 * Pairs with applyApiErrorToForm - return its inverse for known cases so the
 * banner does not double up with the inline field error.
 */
export function isFieldLevelApiError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 409 && err.code && err.code in CONSTRAINT_FIELD_MAP) {
    return true;
  }
  // Pydantic 422 with a detail array — the inline-placement path of
  // applyApiErrorToForm will handle it. Distinguish from the bespoke
  // 422 password_policy_violation shape (detail is an OBJECT, not
  // array — that path stays banner-friendly).
  if (err.status === 422) {
    const body = err.body as { detail?: unknown } | null;
    if (Array.isArray(body?.detail)) return true;
  }
  return false;
}

/**
 * Task #155 (2026-06-02) — Extract password-policy rule violations from a 422
 * response so callers can render them inline on a password field rather than
 * surface a generic "could not create user" alert.
 *
 * Backend shape (app/services/password_policy.py +
 * app/routers/users.py::_enforce_password_policy):
 *   HTTP 422
 *   body: { detail: { code: "password_policy_violation",
 *                     errors: [{code: "too_short", message: "..."}, ...] } }
 *
 * Returns the array of human-readable messages, or null when the error is
 * not a password_policy_violation (caller falls through to its normal error
 * handler — alert/banner/etc.).
 */
export function getPasswordPolicyErrors(err: unknown): string[] | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 422) return null;
  const body = err.body;
  if (!body || typeof body !== 'object') return null;
  // body shape: { detail: { code, errors } }
  const detail = (body as { detail?: unknown }).detail;
  if (!detail || typeof detail !== 'object') return null;
  const code = (detail as { code?: unknown }).code;
  if (code !== 'password_policy_violation') return null;
  const errors = (detail as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return null;
  const messages: string[] = [];
  for (const e of errors) {
    if (e && typeof e === 'object' && 'message' in e) {
      const m = (e as { message?: unknown }).message;
      if (typeof m === 'string' && m.length > 0) messages.push(m);
    }
  }
  return messages.length > 0 ? messages : null;
}
